/**
 * Client-side financial KPI calculator.
 * Ported from backend business_assumptions.py get_report_specific_kpis().
 * All derived metrics are pure functions of raw inputs + base revenue.
 */

export interface DerivedKPIs {
  annual_operating_expenses_total: number;
  mortgage_annual: number;
  taxes_total: number;
  startup_costs_total: number;
  total_cash_invested: number;
  total_expenses: number;
  net_operating_income: number;
  yearly_net_profit_before_tax: number;
  monthly_profit: number;
  cash_on_cash_return: number | null;
  cap_rate: number | null;
  gross_rental_yield: number | null;
  net_rental_yield: number | null;
  dscr: number | null;
  annual_return_rate: number | null;
  total_profit_when_sold: number | null;
  ten_years_return_rate: Record<string, number>;
  five_years_cash_flow: Record<string, number>;
  breakeven_analysis_24_months: Record<string, any>;
  management_fee_dollar: number;
  opex_excluding_mgmt_fee: number;
  owner_payout: number;
}

function round2(val: number): number {
  return Math.round(val * 100) / 100;
}

function val(v: any, fallback = 0): number {
  return typeof v === 'number' && !isNaN(v) ? v : fallback;
}

/** Fraction of the year the listing can operate as a rental (0–1). Owner-occupied days clamped to 0–365. */
export function operatingDaysFactor(ownerOccupiedDays: number): number {
  const d = val(ownerOccupiedDays);
  const clamped = Math.min(365, Math.max(0, d));
  return (365 - clamped) / 365;
}

function sumOperatingExpenses(inputs: Record<string, any>, annualRevenue: number): number {
  const dollarKeys = [
    'lodging_tax', 'maintenance_annual', 'insurance_annual',
    'electricity_annual', 'wifi_annual', 'gas_annual', 'hoa_fee_annual',
    'marketing', 'tech', 'vacancy_cost', 'others_annual',
  ];
  const dollarSum = dollarKeys.reduce((sum, k) => sum + val(inputs[k]), 0);
  const mgmtPct = Math.min(val(inputs.management_fee), 40);
  const managementFeeDollar = (mgmtPct / 100) * annualRevenue;
  return Math.round(dollarSum + managementFeeDollar);
}

function calculateMortgage(inputs: Record<string, any>): number {
  if (!inputs.is_loan_taken) return 0;

  const loanTenure = val(inputs.loan_tenure, 30);
  const downPayment = val(inputs.down_payment);
  const annualInterestPercent = val(inputs.interest_rate, 7.1);
  const propertyPrice = val(inputs.property_price);

  if (loanTenure <= 0 || annualInterestPercent <= 0) return 0;

  const monthlyInterestRate = annualInterestPercent / 12 / 100;
  const principal = propertyPrice - downPayment;
  const n = loanTenure * 12;

  if (principal <= 0 || monthlyInterestRate <= 0) return 0;

  const compounding = Math.pow(1 + monthlyInterestRate, n);
  const monthlyPayment = (monthlyInterestRate * principal * compounding) / (compounding - 1);

  return Math.round(monthlyPayment * 12);
}

function calculateTaxes(inputs: Record<string, any>): number {
  return Math.round(
    val(inputs.property_tax) + val(inputs.income_tax) - val(inputs.depreciation)
  );
}

function sumStartupCosts(inputs: Record<string, any>): number {
  return val(inputs.furnishing_cost) + val(inputs.closing_cost) + val(inputs.remodelling);
}

function calculateTotalInvestment(inputs: Record<string, any>, startupCosts: number): number {
  if (inputs.is_loan_taken) {
    return val(inputs.down_payment) + startupCosts;
  }
  return val(inputs.property_price) + startupCosts;
}

function getTotalRentalProfitOverTime(
  annualRevenue: number, opex: number, mortgagePay: number, years: number
): number {
  const yearlyProfit = annualRevenue - opex - mortgagePay;
  return yearlyProfit * years;
}

function calculateNYearsROI(
  totalCashInvested: number, annualRevenue: number, opex: number,
  mortgagePay: number, appreciationRate: number, propertyPrice: number, timePeriod: number
): { annualizedROI: number | null; totalGain: number | null; yearlyRates: Record<string, number> } {
  const yearlyRates: Record<string, number> = {};

  if (totalCashInvested === 0 || timePeriod === 0) {
    return { annualizedROI: null, totalGain: null, yearlyRates };
  }

  for (let i = 1; i <= timePeriod; i++) {
    const cumulativeProfit = getTotalRentalProfitOverTime(annualRevenue, opex, mortgagePay, i);
    const salePrice = propertyPrice * Math.pow(1 + appreciationRate / 100, i);
    const totalProfit = cumulativeProfit + salePrice - totalCashInvested;
    yearlyRates[`Y${i}`] = round2((totalProfit / totalCashInvested) * 100);
  }

  const totalRentalProfit = getTotalRentalProfitOverTime(annualRevenue, opex, mortgagePay, timePeriod);
  const salePrice = propertyPrice * Math.pow(1 + appreciationRate / 100, timePeriod);
  const totalGain = Math.round(totalRentalProfit + salePrice - totalCashInvested);
  const totalReturn = (totalGain / totalCashInvested) * 100;
  const annualizedROI = round2(totalReturn / timePeriod);

  return { annualizedROI, totalGain, yearlyRates };
}

function calculate5YearsCashFlow(
  annualRevenue: number, opex: number, mortgagePay: number, annualRent: number
): Record<string, number> {
  const result: Record<string, number> = {};
  let cumulative = 0;
  for (let i = 1; i <= 5; i++) {
    const noi = annualRevenue - opex;
    const yearlyProfit = noi - mortgagePay - annualRent;
    cumulative += yearlyProfit;
    result[`Y${i}`] = Math.round(cumulative);
  }
  return result;
}

function calculate24MonthsBreakeven(
  annualRevenue: number, opex: number, monthlyRent: number,
  startupCosts: number, yearlyNetProfit: number
): Record<string, any> {
  const monthlyRevenue = annualRevenue / 12;
  const monthlyOpex = opex / 12;
  const monthlyNetProfit = yearlyNetProfit / 12;

  const periods = [3, 6, 12, 24];
  const result: Record<string, any> = {};

  for (const period of periods) {
    const totalRevenue = monthlyRevenue * period;
    const totalOpex = monthlyOpex * period;
    const totalExpenses = totalOpex + (monthlyRent * period);
    const totalNetProfit = monthlyNetProfit * period;
    const breakevenValue = -(startupCosts - totalNetProfit);

    result[`${period} months`] = {
      total_revenue: Math.round(totalRevenue),
      total_expenses: Math.round(totalExpenses),
      total_net_profit_before_tax: Math.round(totalNetProfit),
      breakeven_value: Math.round(breakevenValue),
    };
  }
  return result;
}

export function calculateDerivedKPIs(
  inputs: Record<string, any>,
  baseRevenue: number
): DerivedKPIs {
  const propertyPrice = val(inputs.property_price);
  const monthlyRent = val(inputs.monthly_rent);
  const ownerOccupiedDays = val(inputs.owner_occupied_days);
  const appreciationRate = val(inputs.property_appreciation_rate, 4);
  const costToSell = val(inputs.cost_to_sell);
  const holdingPeriod = Math.max(val(inputs.holding_period_in_years, 10), 1);
  const wannaSell = inputs.wanna_sell ? true : false;

  const factor = operatingDaysFactor(ownerOccupiedDays);
  const revenue = Math.round(baseRevenue * factor);
  const opexFull = sumOperatingExpenses(inputs, baseRevenue);
  const opex = Math.round(opexFull * factor);
  const mortgage = calculateMortgage(inputs);
  const taxes = calculateTaxes(inputs);
  const startupCosts = sumStartupCosts(inputs);
  const totalCashInvested = calculateTotalInvestment(inputs, startupCosts);
  const annualRent = monthlyRent * 12;

  const mgmtPct = Math.min(val(inputs.management_fee), 40);
  const managementFeeDollar = Math.round((mgmtPct / 100) * revenue);
  const opexExcludingMgmtFee = opex - managementFeeDollar;

  const noi = revenue - opex;
  const netProfit = noi - mortgage - annualRent;
  const totalExpenses = opex + mortgage + taxes + annualRent;

  const cashOnCash = totalCashInvested > 0 ? round2((netProfit / totalCashInvested) * 100) : null;
  const capRate = propertyPrice > 0 ? round2(noi / propertyPrice * 100) : null;
  const grossYield = propertyPrice > 0 ? round2((revenue / propertyPrice) * 100) : null;
  const netYield = propertyPrice > 0 ? round2((noi / propertyPrice) * 100) : null;
  const dscr = mortgage > 0 ? round2(noi / mortgage) : null;


  let { annualizedROI: defaultROI, totalGain: defaultTotalGain, yearlyRates: tenYearsRates } =
    calculateNYearsROI(totalCashInvested, revenue, opex, mortgage, appreciationRate, propertyPrice, 10);

  let annualReturnRate = defaultROI;
  let totalProfitWhenSold = defaultTotalGain;

  if (wannaSell) {
    const result = calculateNYearsROI(
      totalCashInvested, revenue, opex, mortgage, appreciationRate, propertyPrice, holdingPeriod
    );
    annualReturnRate = result.annualizedROI;
    totalProfitWhenSold = result.totalGain != null ? Math.round(result.totalGain - costToSell) : null;
  }

  const fiveYearsCF = calculate5YearsCashFlow(revenue, opex, mortgage, annualRent);
  const breakeven = calculate24MonthsBreakeven(revenue, opex, monthlyRent, startupCosts, netProfit);

  return {
    annual_operating_expenses_total: opex,
    mortgage_annual: mortgage,
    taxes_total: taxes,
    startup_costs_total: startupCosts,
    total_cash_invested: totalCashInvested,
    total_expenses: totalExpenses,
    net_operating_income: noi,
    yearly_net_profit_before_tax: netProfit,
    monthly_profit: Math.round(netProfit / 12),
    cash_on_cash_return: cashOnCash,
    cap_rate: capRate,
    gross_rental_yield: grossYield,
    net_rental_yield: netYield,
    dscr,
    annual_return_rate: annualReturnRate,
    total_profit_when_sold: totalProfitWhenSold,
    ten_years_return_rate: tenYearsRates,
    five_years_cash_flow: fiveYearsCF,
    breakeven_analysis_24_months: breakeven,
    management_fee_dollar: managementFeeDollar,
    opex_excluding_mgmt_fee: opexExcludingMgmtFee,
    owner_payout: noi,
  };
}
